/**
 * 発行直後の鍵を 1 回だけ見せる部品。平文は DB に無い（sha256 のみ）ので、閉じたら二度と表示できない。
 * そのことを「警告色 + 明示の文言」で伝え、コピーを 1 クリックにする。
 */
import type { IssuedKey } from '../api';
import { publicApiUrl } from '../api';
import { translate, useT } from '../i18n';
import { CodeBlock, CopyButton } from './ui';

export function KeyLine({ k }: { k: IssuedKey }) {
  const secret = k.kind === 'secret';
  const { t } = useT();
  return (
    <div className={`keyline ${secret ? 'keyline-secret' : ''}`}>
      <div className="keyline-meta">
        <span className={`pill ${secret ? 'pill-danger' : 'pill-ok'}`}>{t(secret ? 'key.kind.secret' : 'key.kind.publishable')}</span>
        <span className="keyline-env">{t(`env.inline.${k.environment}`)}</span>
        <span className="muted">{t(secret ? 'key.secretNote' : 'key.publishableNote')}</span>
      </div>
      <div className="keyline-value">
        <code>{k.token}</code>
        <CopyButton text={k.token} />
      </div>
    </div>
  );
}

/**
 * ゲームに貼るスニペット。コードはそのまま、コメントだけ表示中の言語にする。
 * saves の行はコメントにしてある: 機能は既定 OFF（task/toggles）なので、作ったばかりのプロジェクトで貼ってそのまま動かすと
 * saves.set が 403 になっていた（dashboard-review 2026-09-27）。ゲストのログインは常に ON なのでそのまま動く
 */
export function quickstart(pk: string) {
  return `<script src="${publicApiUrl()}/sdk.js"></script>
<script type="module">
  const kumo = await Kumo.init({ projectKey: '${pk}' });
  await kumo.auth.guest();              // ${translate('key.quickstartComment')}
  // ${translate('key.quickstartNext')}
  // await kumo.saves.set('slot1', { level: 1 });
</script>`;
}

export function SecretWarning() {
  const { t } = useT();
  return (
    <div className="alert alert-warn" role="note">
      <strong>{t('key.secretWarnTitle')}</strong>
      <span>{t('key.secretWarnBody')}</span>
    </div>
  );
}

export function KeyReveal({ keys }: { keys: IssuedKey[] }) {
  const devPk = keys.find((k) => k.environment === 'development' && k.kind === 'publishable');
  const { t } = useT();
  return (
    <div className="reveal">
      {/* 「今しか表示されない sk を保存して」は sk があるときだけ（メールが未確認だと作成で sk_ が出ない・2026-09-28 SEC-DEVLOGIN-01） */}
      {keys.some((k) => k.kind === 'secret') && <SecretWarning />}
      <div className="reveal-keys">
        {keys.map((k) => (
          <KeyLine key={k.id} k={k} />
        ))}
      </div>
      {devPk && (
        <>
          <h3 className="section-title">{t('key.quickstart')}</h3>
          <p className="muted">{t('key.quickstartLead')}</p>
          <CodeBlock code={quickstart(devPk.token)} label="index.html" />
        </>
      )}
    </div>
  );
}
